import { useEffect, useRef, useState } from "react";
import { Trash2, GripVertical, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { DualRangeSlider } from "@/components/height-map/DualRangeSlider";
import { ColorPickerPopover } from "@/components/height-map/ColorPickerPopover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const HeightLayerRow = ({
  layer,
  onChange,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging,
  disableSolidToggle,
  showRobloxWaterWarning,
  layerPercentage,
  mapPercentage,
  hasImage,
}) => {
  const update = (patch) => onChange({ ...layer, ...patch });
  const isWaterLayer = layer.name.trim().toLowerCase() === "water";

  const [localColor, setLocalColor] = useState(layer.color);
  const debounceRef = useRef(null);
  useEffect(() => {
    setLocalColor(layer.color);
  }, [layer.color]);

  const handleColorInput = (v) => {
    setLocalColor(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => update({ color: v }), 160);
  };
  const commitColor = (v) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    update({ color: v });
  };

  return (
    <div
      data-testid={`layer-row-${layer.name}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`w-full min-w-0 max-w-full overflow-hidden rounded-xl border bg-[#111217] p-3 space-y-3 ${
        isDragging
          ? "border-rose-500/60 opacity-60"
          : "border-zinc-800/70"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <button
          data-testid={`layer-drag-${layer.name}`}
          className="shrink-0 p-1 text-zinc-600 hover:text-zinc-300 cursor-grab active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <ColorPickerPopover
          color={localColor}
          onChange={handleColorInput}
          onCommit={commitColor}
          testId={`layer-color-picker-${layer.name}`}
          swatchTestId={`layer-color-${layer.name}`}
        />
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <input
            data-testid={`layer-name-${layer.name}`}
            value={layer.name}
            onChange={(e) => update({ name: e.target.value })}
            className="min-w-0 flex-1 truncate bg-transparent text-sm text-zinc-100 font-medium border-none outline-none focus:ring-0"
          />
        {(showRobloxWaterWarning || isWaterLayer) && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  data-testid="water-layer-warning-badge"
                  role="img"
                  aria-label="Water layer warning"
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-amber-500/35 bg-amber-500/10 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.12)] cursor-help"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs bg-[#141519] border-zinc-700 text-amber-200"
              >
                <div
                  data-testid="water-layer-warning-tooltip"
                  className="text-xs leading-relaxed"
                >
                  <span className="font-semibold text-amber-300">Water Warning:</span>{" "}
                  Avoid using Roblox Terrain <em>Water</em> directly on generated heightmaps for Roblox; procedural deformation can introduce height variations.
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        </div>
        <button
          data-testid={`layer-delete-${layer.name}`}
          onClick={onDelete}
          className="ml-auto shrink-0 p-1.5 rounded-md text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 transition"
          aria-label="Delete layer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="px-1">
        <div className="flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-widest mb-1.5">
          <span>Min</span>
          <span className="flex items-center gap-1.5 normal-case tracking-normal">
            <EditableRangeValue
              label="Min"
              value={layer.min}
              onCommit={(min) => update({ min })}
              testId={`layer-min-value-${layer.name}`}
            />
            <span className="text-zinc-600">–</span>
            <EditableRangeValue
              label="Max"
              value={layer.max}
              onCommit={(max) => update({ max })}
              testId={`layer-max-value-${layer.name}`}
            />
          </span>
          <span>Max</span>
        </div>
        <DualRangeSlider
          testId={`layer-range-${layer.name}`}
          min={0}
          max={255}
          low={layer.min}
          high={layer.max}
          onChange={(low, high) => update({ min: low, max: high })}
        />
      </div>
    </div>
  );
};

const EditableRangeValue = ({ label, value, onCommit, testId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [editing, value]);

  const cancel = () => {
    setDraft(String(value));
    setEditing(false);
  };
  const commit = () => {
    const parsed = Number(draft);
    if (draft.trim() !== "" && Number.isFinite(parsed)) {
      onCommit(Math.max(0, Math.min(255, Math.round(parsed))));
    } else {
      setDraft(String(value));
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        data-testid={testId}
        type="number"
        min={0}
        max={255}
        step={1}
        inputMode="numeric"
        value={draft}
        autoFocus
        aria-label={`Exact ${label} height value`}
        onFocus={(event) => event.currentTarget.select()}
        onMouseDown={(event) => event.stopPropagation()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") cancel();
        }}
        className="mono h-6 w-12 rounded-md border border-rose-400/50 bg-[#0b0c10] px-1 text-center text-[11px] text-zinc-100 outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-300/30"
      />
    );
  }

  return (
    <button
      type="button"
      data-testid={testId}
      draggable={false}
      title={`Click to type an exact ${label} value`}
      aria-label={`${label} height ${value}. Click to edit.`}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={() => setEditing(true)}
      className="mono min-w-8 rounded-md border border-zinc-800 bg-[#0b0c10] px-1.5 py-0.5 text-[11px] text-zinc-300 hover:border-rose-400/40 hover:text-zinc-100"
    >
      {value}
    </button>
  );
};
